import type { ClassroomDeletionImpact } from "../lib/classroom-deletion";
import { ClassroomDeletionConfirmation } from "./classrooms-admin/ClassroomDeletionConfirmation";
import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronRight,
  BookOpen,
  Download,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useHeaderActions } from "../context/HeaderActionsContext";
import {
  apiService,
  type BulkAssignStudentsRequest,
  type ClassroomImportResponse,
  type ClassroomRelationship,
  type CreateClassroomRequest,
  type ExportResponse,
  type ImportRow,
  type Student,
  type Teacher,
  type UpdateClassroomRequest,
} from "../services/api.service";
import { ClassroomCreateImportDialog } from "./classrooms-admin/ClassroomCreateImportDialog";
import { parseClassroomImportCsv } from "../lib/classroom-import";
import {
  formatClassroomAssignmentsLabel,
  formatTeacherRelationshipDisplay,
  formatTeacherSelectLabel,
} from "./classrooms-admin/formatters";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { curriculumService, type CurriculumSummary } from "../services/curriculum.service";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type ClassroomDraft = {
  name: string;
  description: string;
  teacher_id: number | null;
};

const BULK_TEACHER_LIMIT = 500;
const TEACHER_ASSIGNMENTS_PAGE_SIZE = 40;

const toPositiveInt = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return undefined;
};

const normalizeImportRowsFromJson = (value: unknown): ImportRow[] => {
  const rows = Array.isArray(value)
    ? value
    : value &&
        typeof value === "object" &&
        Array.isArray((value as { rows?: unknown[] }).rows)
      ? (value as { rows: unknown[] }).rows
      : [];

  return rows
    .map<ImportRow | null>((row) => {
      if (!row || typeof row !== "object") {
        return null;
      }
      const item = row as Record<string, unknown>;
      const studentIds = Array.isArray(item.student_ids)
        ? [
            ...new Set(
              item.student_ids
                .map((studentId) => toPositiveInt(studentId))
                .filter((studentId): studentId is number => studentId !== undefined),
            ),
          ]
        : undefined;

      const classroomName =
        typeof item.classroom_name === "string" && item.classroom_name.trim()
          ? item.classroom_name.trim()
          : undefined;
      const description =
        item.description === undefined
          ? undefined
          : item.description === null
            ? null
            : String(item.description);

      const classroomId = toPositiveInt(item.classroom_id);
      const teacherId =
        item.teacher_id === null ? null : toPositiveInt(item.teacher_id);

      if (!classroomId && !classroomName) {
        return null;
      }

      return {
        classroom_id: classroomId,
        classroom_name: classroomName,
        description,
        teacher_id: teacherId,
        student_ids: studentIds,
      } satisfies ImportRow;
    })
    .filter((row): row is ImportRow => row !== null);
};

const downloadBlob = (filename: string, blob: Blob): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
};

export const ClassroomsAdmin = () => {
  const [importedClassroomIds, setImportedClassroomIds] = useState<number[]>([]);
  const [relationships, setRelationships] = useState<ClassroomRelationship[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [teachersLoaded, setTeachersLoaded] = useState(false);
  const [isTeachersLoading, setIsTeachersLoading] = useState(false);
  const [loadedStudentOrganization, setLoadedStudentOrganization] = useState<string | null>(null);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const [searchFilters, setSearchFilters] = useState<FilterValues>({});
  const [searchOrganizations, setSearchOrganizations] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { apiService.getOrganizations().then(setSearchOrganizations).catch(() => {}); }, []);
  const [classroomQuery, setClassroomQuery] = useState("");
  const deferredClassroomQuery = useDeferredValue(classroomQuery);

  const [isCreateImportOpen, setIsCreateImportOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deletionImpact, setDeletionImpact] = useState<ClassroomDeletionImpact | null>(null);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [deletionCheck, setDeletionCheck] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createTeacherId, setCreateTeacherId] = useState("none");
  const [createTeacherSearch, setCreateTeacherSearch] = useState("");

  const [selectedClassroomId, setSelectedClassroomId] = useState<number | null>(
    null,
  );
  const [sheetTeacherSearch, setSheetTeacherSearch] = useState("");
  const [classroomDrafts, setClassroomDrafts] = useState<
    Record<number, ClassroomDraft>
  >({});

  const [isBulkStudentsOpen, setIsBulkStudentsOpen] = useState(false);
  const [bulkClassroomSearch, setBulkClassroomSearch] = useState("");
  const [bulkStudentClassroomId, setBulkStudentClassroomId] = useState("none");
  const [bulkMode, setBulkMode] =
    useState<BulkAssignStudentsRequest["mode"]>("add");
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(
    new Set(),
  );
  const [studentFilters, setStudentFilters] = useState<FilterValues>({});
  const studentOrganization = studentFilters.organization_id || "";
  const studentsLoaded = loadedStudentOrganization === studentOrganization;
  const [studentSearch, setStudentSearch] = useState("");
  const deferredStudentSearch = useDeferredValue(studentSearch);
  const [studentListScrollTop, setStudentListScrollTop] = useState(0);

  const [isBulkTeachersOpen, setIsBulkTeachersOpen] = useState(false);
  const [teacherAssignments, setTeacherAssignments] = useState<
    Record<number, string>
  >({});
  const [teacherAssignmentQuery, setTeacherAssignmentQuery] = useState("");
  const [bulkTeacherSearch, setBulkTeacherSearch] = useState("");
  const deferredBulkTeacherSearch = useDeferredValue(bulkTeacherSearch);
  const [bulkTeacherOptions, setBulkTeacherOptions] = useState<Teacher[]>([]);
  const [isBulkTeacherOptionsLoading, setIsBulkTeacherOptionsLoading] =
    useState(false);
  const [teacherAssignmentOffset, setTeacherAssignmentOffset] = useState(0);

  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importFileName, setImportFileName] = useState("");
  const [importStudentMode, setImportStudentMode] = useState<"add" | "replace">(
    "add",
  );
  const [importPreview, setImportPreview] =
    useState<ClassroomImportResponse | null>(null);
  const [isImportWarningOpen, setIsImportWarningOpen] = useState(false);
  const [isImportSubmitting, setIsImportSubmitting] = useState(false);

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { setActions } = useHeaderActions();

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const relationshipsData = await apiService.getClassroomRelationships(
        undefined,
        { include_students: true },
      );
      setRelationships(relationshipsData);
      setLoadError(null);
    } catch (error) {
      console.error(error);
      setLoadError("Classroom data could not be loaded. Try again.");
      toast.error("Failed to load classroom data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const ensureStudentsLoaded = useCallback(async () => {
    if (studentsLoaded || isStudentsLoading) {
      return;
    }
    try {
      setIsStudentsLoading(true);
      const studentsData = await apiService.getStudents(studentOrganization || undefined);
      setStudents(studentsData);
      setLoadedStudentOrganization(studentOrganization);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load students");
    } finally {
      setIsStudentsLoading(false);
    }
  }, [studentsLoaded, isStudentsLoading, studentOrganization]);

  const ensureTeachersLoaded = useCallback(async () => {
    if (teachersLoaded || isTeachersLoading) {
      return;
    }
    try {
      setIsTeachersLoading(true);
      const teachersData = await apiService.getTeachersForFilter();
      setTeachers(teachersData);
      setTeachersLoaded(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load teachers");
    } finally {
      setIsTeachersLoading(false);
    }
  }, [teachersLoaded, isTeachersLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const drafts: Record<number, ClassroomDraft> = {};
    const assignmentState: Record<number, string> = {};
    for (const relationship of relationships) {
      drafts[relationship.classroom_id] = {
        name: relationship.classroom_name,
        description: relationship.description ?? "",
        teacher_id: relationship.teacher_id,
      };
      assignmentState[relationship.classroom_id] =
        relationship.teacher_id === null
          ? ""
          : relationship.teacher_id.toString();
    }
    setClassroomDrafts(drafts);
    setTeacherAssignments(assignmentState);
  }, [relationships]);

  useEffect(() => {
    setActions(
      <>
        <Button
          variant="outline"
          size="icon"
          onClick={loadData}
          disabled={isLoading || isWorking}
          className="h-8 w-8"
        >
          <RefreshCw
            className={`h-4 w-4 ${isLoading || isWorking ? "animate-spin" : ""}`}
          />
        </Button>
        <Button
          variant="outline"
          className="h-8 gap-2"
          onClick={() => setIsImportOpen(true)}
        >
          <Upload className="h-4 w-4" />
          Import roster
        </Button>
        <Button variant="outline" className="h-8 gap-2" onClick={() => setIsCreateImportOpen(true)}><Upload className="h-4 w-4" />Import classrooms</Button>
        <Button className="h-8 gap-2" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          New classroom
        </Button>
      </>,
    );
    return () => setActions(null);
  }, [setActions, loadData, isLoading, isWorking]);

  useEffect(() => {
    const handleGlobalSlash = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;
      if (isTypingTarget) {
        return;
      }
      if (event.key === "/") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleGlobalSlash);
    return () => window.removeEventListener("keydown", handleGlobalSlash);
  }, []);

  useEffect(() => {
    if (isBulkStudentsOpen || deferredStudentSearch.trim().length > 0) {
      void ensureStudentsLoaded();
    }
  }, [isBulkStudentsOpen, deferredStudentSearch, ensureStudentsLoaded]);

  useEffect(() => {
    if (isCreateOpen || isCreateImportOpen || selectedClassroomId !== null) {
      void ensureTeachersLoaded();
    }
  }, [isCreateOpen, isCreateImportOpen, selectedClassroomId, ensureTeachersLoaded]);

  useEffect(() => {
    if (!isBulkTeachersOpen) {
      return;
    }
    // Seed teachers from relationships so currently-assigned teachers are
    // always available in teacherById even before their page is loaded.
    setTeachers((current) => {
      const byId = new Map(current.map((t) => [t.teacher_id, t]));
      for (const r of relationships) {
        if (r.teacher_id !== null && !byId.has(r.teacher_id)) {
          byId.set(r.teacher_id, {
            teacher_id: r.teacher_id,
            name: r.teacher_name ?? `Teacher #${r.teacher_id}`,
            username: r.teacher_username ?? null,
            email: r.teacher_email ?? null,
          } as Teacher);
        }
      }
      return [...byId.values()];
    });
    let cancelled = false;
    const loadBulkTeacherOptions = async () => {
      try {
        setIsBulkTeacherOptionsLoading(true);
        const page = await apiService.getTeachersForFilterPage({
          query: deferredBulkTeacherSearch.trim() || undefined,
          limit: BULK_TEACHER_LIMIT,
          offset: 0,
        });
        if (cancelled) {
          return;
        }
        setBulkTeacherOptions(page.items);
        setTeachers((current) => {
          const byId = new Map(current.map((teacher) => [teacher.teacher_id, teacher]));
          for (const teacher of page.items) {
            byId.set(teacher.teacher_id, teacher);
          }
          return [...byId.values()];
        });
      } catch (error) {
        console.error(error);
        toast.error("Failed to load teachers");
      } finally {
        if (!cancelled) {
          setIsBulkTeacherOptionsLoading(false);
        }
      }
    };
    void loadBulkTeacherOptions();
    return () => {
      cancelled = true;
    };
  }, [isBulkTeachersOpen, deferredBulkTeacherSearch]);

  useEffect(() => {
    setTeacherAssignmentOffset(0);
  }, [teacherAssignmentQuery]);

  const filteredClassrooms = useMemo(() => {
    const query = deferredClassroomQuery.trim().toLowerCase();
    return [...relationships].sort((a, b) => Number(importedClassroomIds.includes(b.classroom_id)) - Number(importedClassroomIds.includes(a.classroom_id))).filter((relationship) =>
      (!query || relationship.classroom_name.toLowerCase().includes(query) || String(relationship.classroom_id).includes(query)) &&
      (!searchFilters.organization_id || relationship.organization_id === searchFilters.organization_id) &&
      (!searchFilters.teacher_id || String(relationship.teacher_id) === searchFilters.teacher_id),
    );
  }, [relationships, deferredClassroomQuery, searchFilters, importedClassroomIds]);

  const bulkClassroomOptions = useMemo(() => {
    const search = bulkClassroomSearch.trim().toLowerCase();
    if (!search) return relationships;
    return relationships.filter(
      (r) =>
        r.classroom_name.toLowerCase().includes(search) ||
        r.classroom_id.toString().includes(search),
    );
  }, [relationships, bulkClassroomSearch]);

  const teacherById = useMemo(() => {
    const byId = new Map<number, Teacher>();
    for (const teacher of teachers) {
      byId.set(teacher.teacher_id, teacher);
    }
    return byId;
  }, [teachers]);

  const filterTeachers = useCallback(
    (query: string) => {
      const search = query.trim().toLowerCase();
      if (!search) {
        return teachers;
      }
      return teachers.filter(
        (teacher) =>
          teacher.name.toLowerCase().includes(search) ||
          teacher.teacher_id.toString().includes(search) ||
          (teacher.email?.toLowerCase().includes(search) ?? false),
      );
    },
    [teachers],
  );

  const createTeacherOptions = useMemo(
    () => filterTeachers(createTeacherSearch),
    [filterTeachers, createTeacherSearch],
  );

  const sheetTeacherOptions = useMemo(
    () => filterTeachers(sheetTeacherSearch),
    [filterTeachers, sheetTeacherSearch],
  );

  const selectedClassroom = useMemo(
    () =>
      selectedClassroomId === null
        ? null
        : relationships.find((item) => item.classroom_id === selectedClassroomId) ??
          null,
    [relationships, selectedClassroomId],
  );
  const selectedTeacherDisplay = selectedClassroom
    ? formatTeacherRelationshipDisplay({
        teacherId: selectedClassroom.teacher_id,
        teacherName: selectedClassroom.teacher_name,
        teacherUsername: selectedClassroom.teacher_username,
        teacherEmail: selectedClassroom.teacher_email,
      })
    : null;

  const filteredStudents = useMemo(() => {
    if (!studentsLoaded) {
      return [];
    }
    const query = deferredStudentSearch.trim().toLowerCase();
    return students.filter((student) =>
      (!studentFilters.organization_id || student.organization_id === studentFilters.organization_id) &&
      (!query || student.name.toLowerCase().includes(query) || (student.email?.toLowerCase().includes(query) ?? false) || String(student.student_id).includes(query)),
    );
  }, [studentsLoaded, students, deferredStudentSearch, studentFilters]);

  const STUDENT_ROW_HEIGHT = 32;
  const STUDENT_LIST_HEIGHT = 224;
  const STUDENT_OVERSCAN = 8;
  const virtualizedStudents = useMemo(() => {
    const total = filteredStudents.length;
    const startIndex = Math.max(
      0,
      Math.floor(studentListScrollTop / STUDENT_ROW_HEIGHT) - STUDENT_OVERSCAN,
    );
    const visibleCount =
      Math.ceil(STUDENT_LIST_HEIGHT / STUDENT_ROW_HEIGHT) + STUDENT_OVERSCAN * 2;
    const endIndex = Math.min(total, startIndex + visibleCount);
    return {
      totalHeight: total * STUDENT_ROW_HEIGHT,
      startIndex,
      visibleRows: filteredStudents.slice(startIndex, endIndex),
    };
  }, [filteredStudents, studentListScrollTop]);

  const filteredTeacherAssignments = useMemo(() => {
    const query = teacherAssignmentQuery.trim().toLowerCase();
    if (!query) {
      return relationships;
    }
    return relationships.filter(
      (relationship) =>
        relationship.classroom_name.toLowerCase().includes(query) ||
        relationship.classroom_id.toString().includes(query),
    );
  }, [relationships, teacherAssignmentQuery]);

  const pagedTeacherAssignments = useMemo(
    () =>
      filteredTeacherAssignments.slice(
        teacherAssignmentOffset,
        teacherAssignmentOffset + TEACHER_ASSIGNMENTS_PAGE_SIZE,
      ),
    [filteredTeacherAssignments, teacherAssignmentOffset],
  );
  const teacherAssignmentsHasPrev = teacherAssignmentOffset > 0;
  const teacherAssignmentsHasNext =
    teacherAssignmentOffset + TEACHER_ASSIGNMENTS_PAGE_SIZE <
    filteredTeacherAssignments.length;

  const totals = useMemo(() => {
    const classroomCount = relationships.length;
    const relationshipCount = relationships.reduce(
      (count, relationship) => count + relationship.student_count,
      0,
    );
    return { classroomCount, relationshipCount };
  }, [relationships]);

  const updateDraft = (classroomId: number, updates: Partial<ClassroomDraft>) => {
    setClassroomDrafts((current) => {
      const existing = current[classroomId] ?? {
        name: "",
        description: "",
        teacher_id: null,
      };
      return {
        ...current,
        [classroomId]: { ...existing, ...updates },
      };
    });
  };

  useEffect(() => {
    if (!isDeleteOpen || selectedClassroomId === null) return;
    const controller = new AbortController();
    apiService.getClassroomDeletionImpact(selectedClassroomId, controller.signal)
      .then(impact => { if (!controller.signal.aborted) setDeletionImpact(impact); })
      .catch(error => { if (!controller.signal.aborted) setDeletionError(error instanceof Error ? error.message : "Unable to check classroom impact"); });
    return () => controller.abort();
  }, [isDeleteOpen, selectedClassroomId, deletionCheck]);

  const handleDeleteClassroom = async () => {
    if (!selectedClassroom || !deletionImpact || deletionImpact.classroom_id !== selectedClassroom.classroom_id || deletionError || deletionImpact.sync_managed) return;
    setIsWorking(true);
    try {
      await apiService.deleteClassroom(selectedClassroom.classroom_id);
      setRelationships((current) => current.filter((item) => item.classroom_id !== selectedClassroom.classroom_id));
      setSelectedClassroomId(null); setIsDeleteOpen(false);
      toast.success("Classroom deleted. Assignments and history retained.");
    } catch (error) { toast.error(error instanceof Error ? error.message : "Failed to delete classroom"); }
    finally { setIsWorking(false); }
  };

  const handleUnassignStudent = async (classroomId: number, studentId: number) => {
    setIsWorking(true);
    try {
      const result = await apiService.bulkAssignStudents({ classroom_id: classroomId, student_ids: [studentId], mode: "remove" });
      if (result.errors.length) throw new Error(result.errors[0]?.reason || "Unassignment failed");
      toast.success("Student unassigned. Assignments and history retained.");
      await loadData();
    } catch (error) { toast.error(error instanceof Error ? error.message : "Failed to unassign student"); }
    finally { setIsWorking(false); }
  };

  const handleCreateClassroom = async () => {
    const name = createName.trim();
    if (!name) {
      toast.error("Classroom name is required");
      return;
    }
    const teacherId =
      createTeacherId === "none" ? undefined : toPositiveInt(createTeacherId);
    const payload: CreateClassroomRequest = {
      name,
      teacher_id: teacherId ?? null,
    };
    if (createDescription.trim()) {
      payload.description = createDescription.trim();
    }
    try {
      setIsWorking(true);
      await apiService.createClassroom(payload);
      toast.success("Classroom created");
      setCreateName("");
      setCreateDescription("");
      setCreateTeacherId("none");
      setCreateTeacherSearch("");
      setIsCreateOpen(false);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to create classroom");
    } finally {
      setIsWorking(false);
    }
  };

  const handleSaveClassroom = async (classroomId: number) => {
    const draft = classroomDrafts[classroomId];
    if (!draft) {
      return;
    }
    const name = draft.name.trim();
    if (!name) {
      toast.error("Classroom name cannot be empty");
      return;
    }
    const payload: UpdateClassroomRequest = {
      name,
      description: draft.description.trim() ? draft.description.trim() : null,
      teacher_id: draft.teacher_id,
    };
    try {
      setIsWorking(true);
      await apiService.updateClassroom(classroomId, payload);
      toast.success("Classroom updated");
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update classroom");
    } finally {
      setIsWorking(false);
    }
  };

  const handleApplyStudentAssignment = async () => {
    if (bulkStudentClassroomId === "none") {
      toast.error("Select a classroom");
      return;
    }
    if (selectedStudentIds.size === 0 && bulkMode !== "replace") {
      toast.error("Select at least one student");
      return;
    }
    const classroomId = Number.parseInt(bulkStudentClassroomId, 10);
    if (!Number.isInteger(classroomId)) {
      toast.error("Invalid classroom");
      return;
    }
    try {
      setIsWorking(true);
      const result = await apiService.bulkAssignStudents({
        classroom_id: classroomId,
        student_ids: [...selectedStudentIds],
        mode: bulkMode,
      });
      toast.success(
        `Done: +${result.added} / -${result.removed} / skipped ${result.skipped}`,
      );
      if (result.errors.length > 0) {
        toast.error(result.errors[0]?.reason ?? "Some assignment rows failed");
      }
      setIsBulkStudentsOpen(false);
      setSelectedStudentIds(new Set());
      setStudentSearch("");
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to assign students");
    } finally {
      setIsWorking(false);
    }
  };

  const handleApplyTeacherAssignments = async () => {
    try {
      setIsWorking(true);
      const assignments = relationships.map((relationship) => ({
        classroom_id: relationship.classroom_id,
        teacher_id: toPositiveInt(teacherAssignments[relationship.classroom_id]) ?? null,
      }));
      const result = await apiService.bulkAssignTeachers({ assignments });
      toast.success(
        `Teacher updates: ${result.updated}, skipped ${result.skipped}`,
      );
      if (result.errors.length > 0) {
        toast.error(result.errors[0]?.reason ?? "Some teacher rows failed");
      }
      setIsBulkTeachersOpen(false);
      setBulkTeacherSearch("");
      setTeacherAssignmentOffset(0);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to assign teachers");
    } finally {
      setIsWorking(false);
    }
  };

  const handleExport = async (format: "json" | "csv") => {
    try {
      setIsWorking(true);
      const exported = await apiService.exportClassroomRelationships(format);
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      if (format === "csv") {
        downloadBlob(
          `classroom-relationships-${timestamp}.csv`,
          new Blob([typeof exported === "string" ? exported : ""], {
            type: "text/csv;charset=utf-8",
          }),
        );
      } else {
        downloadBlob(
          `classroom-relationships-${timestamp}.json`,
          new Blob([JSON.stringify(exported as ExportResponse, null, 2)], {
            type: "application/json",
          }),
        );
      }
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to export");
    } finally {
      setIsWorking(false);
    }
  };

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const text = await file.text();
      let parsedRows: ImportRow[] = [];
      if (file.name.toLowerCase().endsWith(".json")) {
        parsedRows = normalizeImportRowsFromJson(JSON.parse(text));
      } else {
        const parsed = parseClassroomImportCsv(text);
        parsedRows = parsed.rows;
        if (parsed.errors.length > 0) {
          toast.error(parsed.errors[0]?.reason ?? "CSV has invalid rows");
        }
      }
      if (parsedRows.length === 0) {
        toast.error("No valid import rows found");
        return;
      }
      setImportRows(parsedRows);
      setImportFileName(file.name);
      setImportPreview(null);
      toast.success(`Loaded ${parsedRows.length} rows`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to parse import file");
    } finally {
      event.target.value = "";
    }
  };

  const handleRunDryImport = async () => {
    if (importRows.length === 0) {
      toast.error("Load an import file first");
      return;
    }
    try {
      setIsWorking(true);
      const result = await apiService.importClassroomRelationships({
        rows: importRows,
        dry_run: true,
        student_mode: importStudentMode,
      });
      setImportPreview(result);
      toast.success("Dry run complete");
    } catch (error) {
      console.error(error);
      toast.error("Dry run failed");
    } finally {
      setIsWorking(false);
    }
  };

  const handleConfirmImport = async () => {
    if (importRows.length === 0) {
      return;
    }
    try {
      setIsImportSubmitting(true);
      const result = await apiService.importClassroomRelationships({
        rows: importRows,
        dry_run: false,
        confirm_import: true,
        student_mode: importStudentMode,
      });
      setImportPreview(result);
      setIsImportWarningOpen(false);
      setIsImportOpen(false);
      toast.success("Import completed");
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error("Import failed");
    } finally {
      setIsImportSubmitting(false);
    }
  };

  const handleCmdEnter = (
    event: React.KeyboardEvent,
    callback: () => void,
  ): void => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      callback();
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto">
      <Card className="space-y-4 rounded-xl border-border/60 p-4 shadow-sm sm:p-5">
        <div className="grid gap-3 lg:grid-cols-[minmax(360px,1fr)_auto] lg:items-center">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              value={classroomQuery}
              onChange={(event) => setClassroomQuery(event.target.value)}
              placeholder="Search classrooms by name or ID"
              className="pl-9 pr-14"
            />
            <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              /
            </kbd>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <AdvancedOptions values={searchFilters} onChange={setSearchFilters} fields={[
            { key: "organization_id", label: "Organization", options: searchOrganizations.map((org) => ({ value: org.id, label: org.name })) },
            { key: "teacher_id", label: "Teacher owner", options: [...new Map(relationships.filter((item) => item.teacher_id !== null).map((item) => [item.teacher_id!, { value: String(item.teacher_id), label: `${item.teacher_name || item.teacher_email || "Teacher"} (#${item.teacher_id})` }])).values()] },
          ]} />

            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setIsBulkStudentsOpen(true)}
            >
              <Settings2 className="h-4 w-4" />
              Bulk assign students
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setIsBulkTeachersOpen(true)}
            >
              <Settings2 className="h-4 w-4" />
              Bulk assign teachers
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={isWorking}
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("json")}>
                  JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("csv")}>
                  CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          {isLoading ? <Badge variant="outline">Loading...</Badge> : loadError ? (
            <Badge variant="outline">Classroom data unavailable</Badge>
          ) : (
            <>
              <Badge variant="secondary">{totals.classroomCount} classrooms</Badge>
              <Badge variant="secondary">
                {formatClassroomAssignmentsLabel(totals.relationshipCount)}
              </Badge>
            </>
          )}
        </div>
      </Card>

      <CurriculumSummaryPanel />

      <Card className="flex min-h-[320px] shrink-0 flex-1 flex-col overflow-hidden rounded-xl border-border/60 shadow-sm">
        <div className="border-b bg-muted/20 px-4 py-3">
          <h2 className="text-sm font-semibold tracking-tight">Classrooms</h2>
          <p className="text-xs text-muted-foreground">
            Click a row to edit details in a side panel.
          </p>
        </div>
        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-2.5 p-3">
            {loadError ? (
              <div role="alert" className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                <p className="text-sm text-destructive">{loadError}</p>
                <Button variant="outline" onClick={() => void loadData()} disabled={isLoading || isWorking}>
                  <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                  {isLoading ? "Retrying..." : "Retry"}
                </Button>
              </div>
            ) : isLoading && filteredClassrooms.length === 0
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex w-full items-center justify-between rounded-xl border border-border/60 bg-card px-4 py-3"
                  >
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-5 w-10 rounded-md" />
                        <Skeleton className="h-4 w-36" />
                      </div>
                      <Skeleton className="h-3 w-52" />
                    </div>
                    <Skeleton className="h-4 w-4" />
                  </div>
                ))
              : filteredClassrooms.map((item) => (
                  <button
                    key={item.classroom_id}
                    type="button"
                    onClick={() => setSelectedClassroomId(item.classroom_id)}
                    className="group flex w-full items-center justify-between rounded-xl border border-border/60 bg-card px-4 py-3 text-left transition-all duration-150 hover:border-border hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{item.classroom_name}{importedClassroomIds.includes(item.classroom_id) && <Badge variant="secondary" className="ml-2">New</Badge>}</p>
                        <Badge variant="outline" className="h-5 rounded-md px-1.5 text-[10px]">
                          ID {item.classroom_id}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground/90">
                        Teacher:{" "}
                        {
                          formatTeacherRelationshipDisplay({
                            teacherId: item.teacher_id,
                            teacherName: item.teacher_name,
                            teacherUsername: item.teacher_username,
                            teacherEmail: item.teacher_email,
                          }).primary
                        }{" "}
                        • {item.student_count} students
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5" />
                  </button>
                ))}
            {!isLoading && !loadError && filteredClassrooms.length === 0 ? (
              <p className="px-1 py-8 text-center text-sm text-muted-foreground">
                No classrooms found.
              </p>
            ) : null}
          </div>
        </ScrollArea>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent
          className="sm:max-w-lg"
          onKeyDown={(event) => handleCmdEnter(event, handleCreateClassroom)}
        >
          <DialogHeader>
            <DialogTitle>Create classroom</DialogTitle>
            <DialogDescription>
              Minimal setup. Use Cmd/Ctrl+Enter to submit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Classroom name</Label>
              <Input
                value={createName}
                onChange={(event) => setCreateName(event.target.value)}
                placeholder="Example: Grade 8 - A"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={createDescription}
                onChange={(event) => setCreateDescription(event.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Teacher</Label>
              <Select
                value={createTeacherId}
                onValueChange={setCreateTeacherId}
                onOpenChange={(open) => {
                  if (open) {
                    void ensureTeachersLoaded();
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select teacher (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <div className="sticky top-0 z-10 border-b bg-popover p-2">
                    <Input
                      value={createTeacherSearch}
                      onChange={(event) => setCreateTeacherSearch(event.target.value)}
                      placeholder="Search teacher by username/email/id"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    />
                  </div>
                  <SelectItem value="none">No teacher</SelectItem>
                  {createTeacherOptions.map((teacher) => (
                    <SelectItem
                      key={teacher.teacher_id}
                      value={teacher.teacher_id.toString()}
                    >
                      {formatTeacherSelectLabel(teacher)}
                    </SelectItem>
                  ))}
                  {isTeachersLoading ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      Loading teachers...
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateClassroom} disabled={isWorking}>
              {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet
        open={selectedClassroomId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedClassroomId(null);
          }
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl">
          {selectedClassroom ? (
            <>
              <SheetHeader>
                <SheetTitle>{selectedClassroom.classroom_name}</SheetTitle>
                <SheetDescription>
                  ID {selectedClassroom.classroom_id} • {selectedClassroom.student_count} students
                </SheetDescription>
              </SheetHeader>
              <div
                className="space-y-4 px-4"
                onKeyDown={(event) =>
                  handleCmdEnter(event, () =>
                    handleSaveClassroom(selectedClassroom.classroom_id),
                  )
                }
              >
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input
                    value={
                      classroomDrafts[selectedClassroom.classroom_id]?.name ??
                      selectedClassroom.classroom_name
                    }
                    onChange={(event) =>
                      updateDraft(selectedClassroom.classroom_id, {
                        name: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Input
                    value={
                      classroomDrafts[selectedClassroom.classroom_id]
                        ?.description ?? selectedClassroom.description ?? ""
                    }
                    onChange={(event) =>
                      updateDraft(selectedClassroom.classroom_id, {
                        description: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Teacher</Label>
                  <Select
                    value={
                      classroomDrafts[selectedClassroom.classroom_id]?.teacher_id ===
                        null ||
                      classroomDrafts[selectedClassroom.classroom_id]?.teacher_id ===
                        undefined
                        ? "none"
                        : String(
                            classroomDrafts[selectedClassroom.classroom_id]
                              ?.teacher_id,
                          )
                    }
                    onValueChange={(value) =>
                      updateDraft(selectedClassroom.classroom_id, {
                        teacher_id:
                          value === "none" ? null : toPositiveInt(value) ?? null,
                      })
                    }
                    onOpenChange={(open) => {
                      if (open) {
                        void ensureTeachersLoaded();
                      } else {
                        setSheetTeacherSearch("");
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Search by #id, username, or email" />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="sticky top-0 z-10 border-b bg-popover p-2">
                        <Input
                          value={sheetTeacherSearch}
                          onChange={(event) =>
                            setSheetTeacherSearch(event.target.value)
                          }
                          placeholder="Search teacher by username/email/id"
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}
                        />
                      </div>
                      <SelectItem value="none">No teacher</SelectItem>
                      {sheetTeacherOptions.map((teacher) => (
                        <SelectItem
                          key={teacher.teacher_id}
                          value={teacher.teacher_id.toString()}
                        >
                          {formatTeacherSelectLabel(teacher)}
                        </SelectItem>
                      ))}
                      {isTeachersLoading ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          Loading teachers...
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                  {selectedTeacherDisplay ? (
                    <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
                      <p className="font-medium">{selectedTeacherDisplay.primary}</p>
                      {selectedTeacherDisplay.secondary.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          {selectedTeacherDisplay.secondary.map((item) => (
                            <span key={item}>{item}</span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Students</Label>
                    <span className="text-xs text-muted-foreground">
                      {selectedClassroom.student_count} assigned
                    </span>
                  </div>
                  <div className="rounded-md border border-border/60 bg-muted/10">
                    {selectedClassroom.students.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-muted-foreground">
                        No students assigned to this classroom.
                      </p>
                    ) : (
                      <div className="max-h-56 divide-y overflow-auto">
                        {selectedClassroom.students.map((student) => (
                          <div key={student.student_id} className="px-3 py-2">
                            <Button variant="outline" size="sm" className="float-right" disabled={isWorking} onClick={() => handleUnassignStudent(selectedClassroom.classroom_id, student.student_id)}>Unassign</Button>
                            <p className="text-sm font-medium">{student.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {student.email} • ID {student.student_id}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  Tip: Press Cmd/Ctrl+Enter to save quickly.
                </div>
              </div>
              <SheetFooter className="border-t">
                <Button variant="destructive" disabled={isWorking || selectedClassroom.sync_managed} onClick={() => { setDeletionImpact(null); setDeletionError(null); setIsDeleteOpen(true); }}>Delete classroom</Button>
                {selectedClassroom.sync_managed ? <p className="text-xs text-muted-foreground">Remove this sync-managed classroom through its roster source.</p> : null}
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkStudentClassroomId(
                      selectedClassroom.classroom_id.toString(),
                    );
                    setIsBulkStudentsOpen(true);
                  }}
                >
                  Assign students
                </Button>
                <Button
                  onClick={() => handleSaveClassroom(selectedClassroom.classroom_id)}
                  disabled={isWorking}
                >
                  {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save changes
                </Button>
              </SheetFooter>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      <Dialog open={isBulkStudentsOpen} onOpenChange={setIsBulkStudentsOpen}>
        <DialogContent
          className="sm:max-w-2xl"
          onKeyDown={(event) => handleCmdEnter(event, handleApplyStudentAssignment)}
        >
          <DialogHeader>
            <DialogTitle>Bulk assign students</DialogTitle>
            <DialogDescription>
              Select target classroom, choose mode, and apply. Cmd/Ctrl+Enter to
              submit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Select
                value={bulkStudentClassroomId}
                onValueChange={(val) => {
                  setBulkStudentClassroomId(val);
                  setBulkClassroomSearch("");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select classroom" />
                </SelectTrigger>
                <SelectContent>
                  <div className="sticky top-0 z-10 border-b bg-popover p-2">
                    <Input
                      value={bulkClassroomSearch}
                      onChange={(e) => setBulkClassroomSearch(e.target.value)}
                      placeholder="Search classrooms..."
                      className="h-8"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <SelectItem value="none">Select classroom</SelectItem>
                  {bulkClassroomOptions.map((item) => (
                    <SelectItem
                      key={item.classroom_id}
                      value={item.classroom_id.toString()}
                    >
                      {item.classroom_name}
                      <span className="ml-1.5 text-[11px] text-muted-foreground">
                        #{item.classroom_id}
                      </span>
                    </SelectItem>
                  ))}
                  {bulkClassroomOptions.length === 0 && bulkClassroomSearch && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">
                      No classrooms found
                    </div>
                  )}
                </SelectContent>
              </Select>

              <Select
                value={bulkMode}
                onValueChange={(value) =>
                  setBulkMode(value as BulkAssignStudentsRequest["mode"])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add</SelectItem>
                  <SelectItem value="replace">Replace</SelectItem>
                  <SelectItem value="remove">Remove</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Input
              value={studentSearch}
              onChange={(event) => setStudentSearch(event.target.value)}
              placeholder="Search students by name, email, or ID"
              disabled={isStudentsLoading}
            />

            <AdvancedOptions values={studentFilters} onChange={(values) => { setStudentFilters(values); setStudentListScrollTop(0); }} fields={[
              { key: "organization_id", label: "Organization", options: searchOrganizations.map((org) => ({ value: org.id, label: org.name })) },
            ]} />

            <div
              className="rounded-md border p-2"
              style={{ height: `${STUDENT_LIST_HEIGHT + 16}px` }}
            >
              {!studentsLoaded ? (
                <div className="flex h-full items-center justify-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void ensureStudentsLoaded()}
                    disabled={isStudentsLoading}
                  >
                    {isStudentsLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    Load students
                  </Button>
                </div>
              ) : filteredStudents.length === 0 ? (
                <p className="p-2 text-sm text-muted-foreground">No students found.</p>
              ) : (
                <div
                  className="h-full overflow-auto"
                  onScroll={(event) =>
                    setStudentListScrollTop(event.currentTarget.scrollTop)
                  }
                >
                  <div
                    className="relative"
                    style={{ height: `${virtualizedStudents.totalHeight}px` }}
                  >
                    {virtualizedStudents.visibleRows.map((student, index) => {
                      const absoluteIndex = virtualizedStudents.startIndex + index;
                      const isChecked = selectedStudentIds.has(student.student_id);
                      return (
                        <label
                          key={student.student_id}
                          className="absolute left-0 right-0 flex cursor-pointer items-center gap-2.5 rounded px-2 text-sm hover:bg-muted/40"
                          style={{
                            top: `${absoluteIndex * STUDENT_ROW_HEIGHT}px`,
                            height: `${STUDENT_ROW_HEIGHT}px`,
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() =>
                              setSelectedStudentIds((current) => {
                                const next = new Set(current);
                                if (next.has(student.student_id)) {
                                  next.delete(student.student_id);
                                } else {
                                  next.add(student.student_id);
                                }
                                return next;
                              })
                            }
                          />
                          <span className="font-medium">{student.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {student.email}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {studentsLoaded
                  ? `Loaded ${students.length}, showing ${filteredStudents.length}, selected ${selectedStudentIds.size}`
                  : "Students not loaded yet"}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    setSelectedStudentIds(
                      new Set(filteredStudents.map((student) => student.student_id)),
                    )
                  }
                  disabled={!studentsLoaded || filteredStudents.length === 0}
                >
                  Select filtered
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSelectedStudentIds(new Set())}
                  disabled={selectedStudentIds.size === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBulkStudentsOpen(false)}
              disabled={isWorking}
            >
              Cancel
            </Button>
            <Button onClick={handleApplyStudentAssignment} disabled={isWorking}>
              {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isBulkTeachersOpen}
        onOpenChange={(open) => {
          setIsBulkTeachersOpen(open);
          if (!open) {
            setBulkTeacherSearch("");
            setTeacherAssignmentOffset(0);
          }
        }}
      >
        <DialogContent
          className="overflow-hidden sm:max-w-2xl"
          onKeyDown={(event) => handleCmdEnter(event, handleApplyTeacherAssignments)}
        >
          <DialogHeader>
            <DialogTitle>Bulk assign teachers</DialogTitle>
            <DialogDescription>
              Update owner per classroom with searchable teacher options.
            </DialogDescription>
          </DialogHeader>
          <div className="min-w-0 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                value={teacherAssignmentQuery}
                onChange={(event) => setTeacherAssignmentQuery(event.target.value)}
                placeholder="Filter classrooms..."
              />
              <Input
                value={bulkTeacherSearch}
                onChange={(event) => setBulkTeacherSearch(event.target.value)}
                placeholder="Filter teachers by id, username, or email"
              />
            </div>
            <ScrollArea className="h-72 rounded-lg border bg-muted/10 p-2">
              <div className="space-y-2.5">
                {pagedTeacherAssignments.map((item) => {
                  const selectedTeacherId = toPositiveInt(
                    teacherAssignments[item.classroom_id],
                  );
                  const selectedTeacher = selectedTeacherId
                    ? teacherById.get(selectedTeacherId)
                    : null;
                  const hasSelectedInPage = selectedTeacherId
                    ? bulkTeacherOptions.some(
                        (teacher) => teacher.teacher_id === selectedTeacherId,
                      )
                    : false;
                  const rowTeacherOptions =
                    selectedTeacher && !hasSelectedInPage
                      ? [selectedTeacher, ...bulkTeacherOptions]
                      : bulkTeacherOptions;

                  return (
                    <div
                      key={item.classroom_id}
                      className="space-y-2 rounded-lg border border-border/60 bg-card p-3"
                    >
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge
                          variant="outline"
                          className="h-5 rounded-md px-1.5 text-[10px]"
                        >
                          #{item.classroom_id}
                        </Badge>
                        <p className="text-sm font-medium tracking-tight">
                          {item.classroom_name}
                        </p>
                        <Badge
                          variant="secondary"
                          className="h-5 rounded-md px-1.5 text-[10px]"
                        >
                          {item.student_count} students
                        </Badge>
                      </div>
                      <div className="rounded-md bg-muted/35 px-2 py-1.5 text-xs text-muted-foreground">
                        <span className="mr-1 font-medium text-foreground/80">
                          Current
                        </span>
                        <span
                          className="truncate"
                          title={
                            formatTeacherRelationshipDisplay({
                              teacherId: item.teacher_id,
                              teacherName: item.teacher_name,
                              teacherUsername: item.teacher_username,
                              teacherEmail: item.teacher_email,
                            }).primary
                          }
                        >
                          {
                            formatTeacherRelationshipDisplay({
                              teacherId: item.teacher_id,
                              teacherName: item.teacher_name,
                              teacherUsername: item.teacher_username,
                              teacherEmail: item.teacher_email,
                            }).primary
                          }
                        </span>
                      </div>
                      <Select
                        value={
                          teacherAssignments[item.classroom_id]?.trim()
                            ? teacherAssignments[item.classroom_id]
                            : "none"
                        }
                        onValueChange={(value) =>
                          setTeacherAssignments((current) => ({
                            ...current,
                            [item.classroom_id]: value === "none" ? "" : value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select owner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No teacher</SelectItem>
                          {rowTeacherOptions.map((teacher) => (
                            <SelectItem
                              key={teacher.teacher_id}
                              value={teacher.teacher_id.toString()}
                            >
                              {formatTeacherSelectLabel(teacher)}
                            </SelectItem>
                          ))}
                          {isBulkTeacherOptionsLoading ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              Loading teachers...
                            </div>
                          ) : null}
                        </SelectContent>
                      </Select>
                      {selectedTeacher && selectedTeacherId !== item.teacher_id ? (
                        <div className="rounded-md border border-emerald-200/60 bg-emerald-50/40 px-2 py-1.5 text-xs text-emerald-900/80 dark:border-emerald-900/50 dark:bg-emerald-950/20 dark:text-emerald-200/80">
                          <span className="mr-1 font-medium">Selected</span>
                          <span
                            className="truncate"
                            title={formatTeacherSelectLabel(selectedTeacher)}
                          >
                            {formatTeacherSelectLabel(selectedTeacher)}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {pagedTeacherAssignments.length === 0 ? (
                  <p className="p-2 text-sm text-muted-foreground">
                    No classrooms found.
                  </p>
                ) : null}
              </div>
            </ScrollArea>
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!teacherAssignmentsHasPrev}
                onClick={() =>
                  setTeacherAssignmentOffset((current) =>
                    Math.max(0, current - TEACHER_ASSIGNMENTS_PAGE_SIZE),
                  )
                }
              >
                Previous classrooms
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!teacherAssignmentsHasNext}
                onClick={() =>
                  setTeacherAssignmentOffset(
                    (current) => current + TEACHER_ASSIGNMENTS_PAGE_SIZE,
                  )
                }
              >
                Next classrooms
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBulkTeachersOpen(false)}
              disabled={isWorking}
            >
              Cancel
            </Button>
            <Button onClick={handleApplyTeacherAssignments} disabled={isWorking}>
              {isWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ClassroomCreateImportDialog open={isCreateImportOpen} onOpenChange={setIsCreateImportOpen} teachers={teachers} onImported={async (ids) => { setImportedClassroomIds(ids); await loadData(); }} />
      <Dialog open={isDeleteOpen} onOpenChange={(open) => { if (!isWorking) setIsDeleteOpen(open); }}>
        <DialogContent><DialogHeader><DialogTitle>Delete classroom</DialogTitle><DialogDescription>Review existing assignments and student activity before deleting.</DialogDescription></DialogHeader>
          <ClassroomDeletionConfirmation impact={deletionImpact?.classroom_id === selectedClassroomId ? deletionImpact : null} error={deletionError} working={isWorking}
            onRetry={() => { setDeletionImpact(null); setDeletionError(null); setDeletionCheck(value => value + 1); }}
            onCancel={() => setIsDeleteOpen(false)} onDelete={() => void handleDeleteClassroom()} />
        </DialogContent>
      </Dialog>
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent
          className="sm:max-w-xl"
          onKeyDown={(event) =>
            handleCmdEnter(event, () => {
              if (importPreview) {
                setIsImportWarningOpen(true);
              } else {
                void handleRunDryImport();
              }
            })
          }
        >
          <DialogHeader>
            <DialogTitle>Import classroom roster relationships</DialogTitle>
            <DialogDescription>
              Upload a CSV or JSON file with classroom, teacher, and student mappings. Run a dry-run first to preview changes, then commit when the preview looks right.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
              <p className="font-medium">Accepted columns</p>
              <p className="mt-1 text-muted-foreground">
                Use `classroom_id` or `classroom_name`, and optionally `description`,
                `teacher_id`, and `student_ids`.
              </p>
              <p className="mt-1 text-muted-foreground">
                Separate `student_ids` with `|` or `;`. Dry-run previews changes without
                writing anything. Commit import applies the changes for real.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="classroom-import-file">File (.csv or .json)</Label>
              <Input
                id="classroom-import-file"
                type="file"
                accept=".csv,.json"
                onChange={handleImportFileChange}
                disabled={isWorking || isImportSubmitting}
              />
              {importFileName ? (
                <p className="text-xs text-muted-foreground">
                  Loaded {importRows.length} rows from {importFileName}
                </p>
              ) : null}
            </div>

            <Select
              value={importStudentMode}
              onValueChange={(value) =>
                setImportStudentMode(value as "add" | "replace")
              }
              disabled={isWorking || isImportSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Student merge mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="add">Add students to listed classrooms</SelectItem>
                <SelectItem value="replace">Replace students in listed classrooms</SelectItem>
              </SelectContent>
            </Select>

            {importPreview ? (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <p>Rows: {importPreview.requested}</p>
                <p>
                  Classrooms: +{importPreview.created_classrooms} / ~
                  {importPreview.updated_classrooms}
                </p>
                <p>
                  Relationships: +{importPreview.created_relationships} / -
                  {importPreview.removed_relationships}
                </p>
                <p>Skipped: {importPreview.skipped}</p>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportOpen(false)}
              disabled={isWorking || isImportSubmitting}
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={handleRunDryImport}
              disabled={isWorking || isImportSubmitting || importRows.length === 0}
            >
              Run dry-run
            </Button>
            <Button
              onClick={() => setIsImportWarningOpen(true)}
              disabled={
                isWorking ||
                isImportSubmitting ||
                importRows.length === 0 ||
                importPreview === null
              }
            >
              Commit import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isImportWarningOpen} onOpenChange={setIsImportWarningOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm import commit</DialogTitle>
            <DialogDescription>
              This applies real changes. Proceed only after reviewing dry-run.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportWarningOpen(false)}
              disabled={isImportSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmImport} disabled={isImportSubmitting}>
              {isImportSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function CurriculumSummaryPanel() {
  const [curricula, setCurricula] = useState<CurriculumSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    curriculumService
      .list()
      .then(setCurricula)
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <Card className="rounded-xl border-border/60 p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><BookOpen className="size-4 text-lime-700" /><h2 className="text-sm font-semibold">Curriculum</h2></div>
          <p className="mt-1 text-xs text-muted-foreground">Configure organization bindings and lesson hierarchy.</p>
        </div>
        <Button asChild variant="outline" size="sm"><a href="/curriculum">Open curriculum</a></Button>
      </div>
      {isLoading ? <div className="mt-3 flex gap-2" aria-live="polite"><Skeleton className="h-6 w-24" /><Skeleton className="h-6 w-28" /></div> : error ? <p className="mt-3 text-xs text-muted-foreground">Curriculum data unavailable. Open page to retry.</p> : curricula.length === 0 ? <p className="mt-3 text-xs text-muted-foreground">No curricula configured.</p> : <div className="mt-3 flex flex-wrap gap-2">{curricula.slice(0, 4).map((curriculum) => <a key={curriculum.id} href={`/curriculum/${curriculum.id}`} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs hover:border-lime-600/50 hover:bg-lime-600/5"><span className="max-w-40 truncate">{curriculum.name}</span><span className="text-muted-foreground">{curriculum.lesson_count} lessons</span></a>)}{curricula.length > 4 ? <Badge variant="secondary">+{curricula.length - 4} more</Badge> : null}</div>}
    </Card>
  );
}
